import React from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import type { Option } from "@/lib/types";
import { cn } from "@/lib/utils";

interface NormalSelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  options: Option[];
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

const NormalSelect: React.FC<NormalSelectProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  options,
  placeholder = "Select an option",
  label,
  required = false,
  disabled = false,
  error,
  className = "",
}) => {
  const hasError = !!error;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium text-foreground mb-1",
            hasError && "text-red-700",
          )}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          className={cn(
            "w-full px-4 py-2 bg-surface text-foreground border rounded-md shadow-sm sm:text-sm transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:border-transparent appearance-none pr-10",
            hasError
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border-border focus:ring-primary focus:border-primary",
          )}
        >
          <option value="" disabled={required}>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-foreground">
          <ChevronDownIcon
            className="h-5 w-5 text-text-light"
            aria-hidden="true"
          />
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default NormalSelect;
